export const CLAN_LETTERS = 'BCDFGHJKLMNPQRSTVWXYZ'.split('');

// CLAN's own colour-key defaults, distinct from FLAG's (DEFAULT_FLAG_KEY_BINDINGS
// in flagLogic.js) even though both modules share the same Red/Yellow/Green
// zones and normalizer -- CLAN uses the top-row number keys, FLAG stays on the
// number pad.
export const DEFAULT_CLAN_COLOUR_KEY_BINDINGS = {
  RED: 'Digit1',
  YELLOW: 'Digit2',
  GREEN: 'Digit3'
};

export const CLAN_ANSWER_IDS = ['A', 'B', 'C', 'D'];

export const DEFAULT_CLAN_ANSWER_KEY_BINDINGS = {
  A: 'KeyA',
  B: 'KeyB',
  C: 'KeyC',
  D: 'KeyD'
};

const CLAN_ANSWER_FALLBACK_CODES = [
  'KeyA', 'KeyB', 'KeyC', 'KeyD',
  'KeyQ', 'KeyW', 'KeyE', 'KeyR',
  'KeyZ', 'KeyX', 'KeyC', 'KeyV'
];

export const normalizeClanAnswerKeyBindings = (bindings = {}, reservedCodes = []) => {
  const usedCodes = new Set(reservedCodes.filter((code) => typeof code === 'string' && code.trim()));

  return CLAN_ANSWER_IDS.reduce((normalized, answerId) => {
    const storedCode = typeof bindings?.[answerId] === 'string' ? bindings[answerId].trim() : '';
    const candidates = [
      storedCode,
      DEFAULT_CLAN_ANSWER_KEY_BINDINGS[answerId],
      ...CLAN_ANSWER_FALLBACK_CODES
    ];
    const code = candidates.find((candidate) => candidate && !usedCodes.has(candidate));
    normalized[answerId] = code;
    usedCodes.add(code);
    return normalized;
  }, {});
};

export const getClanAnswerForCode = (code, bindings = DEFAULT_CLAN_ANSWER_KEY_BINDINGS) => (
  CLAN_ANSWER_IDS.find((answerId) => bindings?.[answerId] === code) || null
);

export const CLAN_COLOURS = [
  { name: 'RED', value: '#ff3333', contactX: 240 },
  { name: 'YELLOW', value: '#ffff66', contactX: 324 },
  { name: 'GREEN', value: '#66ff66', contactX: 408 }
];

export const CLAN_COLOUR_BAND_WIDTH = 84;

const pick = (values) => values[Math.floor(Math.random() * values.length)];

const shuffle = (values) => {
  const result = [...values];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }
  return result;
};

export const getClanSequenceLength = (difficulty = 'medium') => 4;

export const makeClanSequence = (length = 4) => Array.from(
  { length: 4 },
  () => pick(CLAN_LETTERS)
).join('');

const substituteLetter = (sequence, index) => {
  const current = sequence[index];
  const replacement = pick(CLAN_LETTERS.filter((letter) => letter !== current));
  return `${sequence.slice(0, index)}${replacement}${sequence.slice(index + 1)}`;
};

const swapLetters = (sequence, first, second) => {
  const letters = sequence.split('');
  [letters[first], letters[second]] = [letters[second], letters[first]];
  return letters.join('');
};

export const makeClanLetterOptions = (sequence) => {
  const options = new Set([sequence]);
  let attempt = 0;
  while (options.size < 4 && attempt < 50) {
    if (attempt % 2 === 0) {
      options.add(substituteLetter(sequence, attempt % sequence.length));
    } else {
      const first = attempt % sequence.length;
      const second = (first + 1) % sequence.length;
      const swapped = swapLetters(sequence, first, second);
      options.add(swapped === sequence ? substituteLetter(sequence, first) : swapped);
    }
    attempt += 1;
  }
  return shuffle([...options]).map((value, index) => ({
    id: String.fromCharCode(65 + index),
    value,
    correct: value === sequence
  }));
};

export const makeClanLetterRound = (difficulty = 'medium', configuredLength) => {
  const requestedLength = Number.isFinite(configuredLength)
    ? configuredLength
    : getClanSequenceLength(difficulty);
  const sequence = makeClanSequence(requestedLength);
  return { sequence, options: makeClanLetterOptions(sequence) };
};

export const getClanColourContactX = (colourName) => (
  CLAN_COLOURS.find((colour) => colour.name === colourName)?.contactX ?? null
);

export const isClanColourInResponseWindow = (target, tolerance = 24) => {
  const contactX = getClanColourContactX(target?.colour?.name || target?.colourName);
  return contactX !== null
    && target.x >= contactX - tolerance
    && target.x <= contactX + CLAN_COLOUR_BAND_WIDTH + tolerance;
};

export const hasClanColourPassedResponseWindow = (target, tolerance = 24) => {
  const contactX = getClanColourContactX(target?.colour?.name || target?.colourName);
  return contactX !== null
    && target.x > contactX + CLAN_COLOUR_BAND_WIDTH + tolerance + 5;
};

export const isClanColourTargetVisible = (target, arenaWidth) => (
  Number.isFinite(target?.x)
  && Number.isFinite(arenaWidth)
  && target.x <= arenaWidth + 10
);

export const selectClanTargetForResponse = (targets, colourName, tolerance = 24) => {
  const candidates = targets
    .filter((target) => (
      (target.colour?.name || target.colourName) === colourName
      && isClanColourInResponseWindow(target, tolerance)
    ))
    .sort((first, second) => (
      Math.abs(first.x - getClanColourContactX(first.colour?.name || first.colourName))
      - Math.abs(second.x - getClanColourContactX(second.colour?.name || second.colourName))
    ));
  return candidates[0] || null;
};
